import {noop} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'

import {useAppDispatch} from '../../../hooks/redux'
import {fatalError} from '../../../redux/features/core'
import {closeModal} from '../../../redux/features/modal'

import useAsync from '../../../../../commons/src/hooks/use-async'
import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as optionApi from '../../../services/api/option'
import * as providerInfoApi from '../../../services/api/provider-info'
import {toMixedValidationSchema, toTouchedFormData} from '../../utils/form'

import {ModalMeta, OptionId, ProviderInfo} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import PrintingMethodFormPartial, {
  SingleFormData,
  formDataToPartialRequest,
  formatInfillName,
  initialValues,
  sortInfills,
  updateLinkedInfills,
  validationSchema,
} from '../../provider-info/printing-method-form-partial'

type Props = {
  providerInfos: ProviderInfo[]
  meta: ModalMeta
  onDone?: () => void
}

const AddProviderInfoPrintingMethodModal = ({meta, onDone = noop, providerInfos}: Props) => {
  const dispatch = useAppDispatch()
  const {
    isLoading: isInfillsLoading,
    data: infills = [],
    refresh: refreshInfills,
  } = useAsync(() => optionApi.getInfillsWithLinkedProviderInfos(), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const infillOptions = sortInfills(infills).map(infill => ({
    label: formatInfillName(infill),
    value: infill.id,
  }))

  const formik = useFormik<SingleFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerInfoRequest = formDataToPartialRequest(touchedValues)

      try {
        const providerInfoIds = providerInfos.map(info => info.id)

        if (touchedValues.infillIds) {
          const initialInfillIds = formik.initialValues.infillIds

          await updateLinkedInfills(
            initialInfillIds,
            touchedValues.infillIds as OptionId[],
            providerInfoIds
          )

          refreshInfills()
        }

        await Promise.all(
          providerInfos.map(providerInfo =>
            providerInfoApi.patch(
              {
                ...providerInfoRequest,
                // If we change the provider info the API expects the materialConfigId otherwise we get an error.
                materialConfigId: providerInfo.materialConfigId,
              },
              providerInfo.id
            )
          )
        )

        onDone()
        dispatch(closeModal())
      } catch (error) {
        if ((error as Error).message === 'Unallowed Relation Expression') {
          actions.setFieldError(
            'formikGeneral',
            'Infill options are only supported for FDM configurations'
          )
        } else {
          actions.setFieldError('formikGeneral', (error as Error).message)
        }
      }
    },
  })

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Add printing method" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <PrintingMethodFormPartial
        formik={formik}
        isLoading={isInfillsLoading}
        onRefresh={refreshInfills}
        infillOptions={infillOptions}
        providerInfos={providerInfos}
      />
    </Modal>
  )
}

export default AddProviderInfoPrintingMethodModal
